import { inject, computed, watch, openBlock, createBlock, Teleport, createVNode, toDisplayString, Fragment, renderList, mergeProps, reactive } from 'vue';

const defaultMetadata = {
    titleDefault: '',
    titleSeparator: ' - ',
    title: '',
    description: '',
    image: '',
    keywords: [],
    url: '',
    lang: 'nl',
    languages: ['nl'],
};
function defaultTitleTemplate(metadata) {
    return metadata.title
        ? `${metadata.title}${metadata.titleSeparator}${metadata.titleDefault}`
        : metadata.titleDefault;
}
function getDefaultData(opts = {}) {
    return {
        metadata: {
            ...defaultMetadata,
            ...(opts.metadata ? opts.metadata : {}),
        },
        titleTemplate: typeof opts.titleTemplate === 'function' ? opts.titleTemplate : defaultTitleTemplate,
        schemas: [...(opts.schemas || [])],
    };
}
const METADATA = Symbol('metadata');

function buildMetaTags({ metadata }, title) {
    const response = { meta: [] };
    response.meta = [
        // Title
        { uid: 1, property: 'og:title', content: title },
        { uid: 2, name: 'twitter:title', content: title },
        { uid: 3, itemprop: 'name', content: title },
        // Description
        { uid: 4, name: 'description', content: metadata.description },
        { uid: 5, name: 'keywords', content: Array.isArray(metadata.keywords) ? metadata.keywords.map((it) => it.name).join(', ') : '' },
        { uid: 6, property: 'og:description', content: metadata.description },
        { uid: 7, name: 'twitter:description', content: metadata.description },
        { uid: 8, itemprop: 'description', content: metadata.description },
        // Image
        { uid: 9, property: 'og:image', content: metadata.image },
        { uid: 10, name: 'twitter:image:src', content: metadata.image },
        { uid: 11, itemprop: 'image', content: metadata.image },
        // Sitename
        { uid: 12, property: 'og:site_name', content: metadata.titleDefault },
        // Url
        { uid: 13, property: 'og:url', content: window.location.href },
        { uid: 14, name: 'twitter:site', content: window.location.href },
        // Others
        { uid: 15, name: 'og:locale', content: metadata.lang },
        { uid: 16, name: 'twitter:card', content: 'summary' },
        { uid: 17, name: 'author', content: 'Project Five' },
    ];
    return response;
}
function buildLinkTags({ metadata }) {
    const response = { link: [] };
    const url = window.location.href;
    response.link.push({ rel: 'canonical', href: url });
    if (Array.isArray(metadata.languages) && metadata.languages.length > 1) {
        metadata.languages
            .filter((it) => it !== metadata.lang)
            .forEach((alternatelang) => {
            response.link.push({
                rel: 'alternate',
                hreflang: alternatelang,
                href: url.replace(`/${metadata.lang}/`, `/${alternatelang}/`),
            });
        });
    }
    return response;
}
function buildMetadata(input) {
    const pageTitle = input.titleTemplate(input.metadata);
    return {
        ...buildMetaTags(input, pageTitle),
        ...buildLinkTags(input),
        title: pageTitle,
    };
}
function setSchemas(schemas) {
    const cont = document.body;
    const uniqueClass = 'vSchema';
    // Remove existing
    const currentSchemaElements = cont.querySelectorAll(`.${uniqueClass}`);
    currentSchemaElements.forEach((e) => {
        e.remove();
    });
    // Add new
    schemas.forEach((schema) => {
        const el = document.createElement('script');
        el.classList.add(uniqueClass);
        el.type = 'application/ld+json';
        el.innerHTML = JSON.stringify(schema);
        cont.appendChild(el);
    });
}

var script = {
    name: 'Meta',
    setup() {
        const data = inject('metadataData');
        const metadata = computed(() => buildMetadata(data));

        const documentLang = document.documentElement.getAttribute('lang');
        if (data.metadata && typeof data.metadata.lang === 'string' && data.metadata.lang !== documentLang) {
            document.documentElement.setAttribute('lang', data.metadata.lang);
        }

        watch(() => data.schemas, setSchemas, { immediate: true });

        return {
            data,
            metadata,
        };
    },
};

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return (openBlock(), createBlock(Teleport, { to: "head" }, [
    createVNode("title", null, toDisplayString($setup.metadata.title), 1 /* TEXT */),
    (openBlock(true), createBlock(Fragment, null, renderList($setup.metadata.meta, (item) => {
      return (openBlock(), createBlock("meta", mergeProps({
        key: item.uid
      }, item), null, 16 /* FULL_PROPS */))
    }), 128 /* KEYED_FRAGMENT */)),
    (openBlock(true), createBlock(Fragment, null, renderList($setup.metadata.link, (item) => {
      return (openBlock(), createBlock("link", mergeProps({
        key: item.uid
      }, item), null, 16 /* FULL_PROPS */))
    }), 128 /* KEYED_FRAGMENT */))
  ]))
}

script.render = render;
script.__file = "src/Meta.vue";

var index = {
    install(app, opts = {}) {
        const dd = getDefaultData(opts);
        const defaultData = reactive(dd);
        const externalData = reactive({
            metadata: { ...dd.metadata },
            titleTemplate: dd.titleTemplate,
            schemas: [...dd.schemas],
        });
        function setDefaultMetadata(val, update = true) {
            setMetadata(val, update, true);
        }
        function resetMetadata() {
            Object.assign(externalData, defaultData);
        }
        function setMetadata(val, update = true, setAsDefault = false) {
            if (val.metadata) {
                if (setAsDefault) {
                    defaultData.metadata = {
                        ...defaultData.metadata,
                        ...val.metadata,
                    };
                }
                /* istanbul ignore else */
                if (update) {
                    externalData.metadata = {
                        ...defaultData.metadata,
                        ...val.metadata,
                    };
                }
            }
            if (val.titleTemplate && typeof val.titleTemplate === 'function') {
                if (setAsDefault) {
                    defaultData.titleTemplate = val.titleTemplate;
                }
                /* istanbul ignore else */
                if (update) {
                    externalData.titleTemplate = val.titleTemplate;
                }
            }
            if (val.schemas) {
                /* istanbul ignore else */
                if (setAsDefault) {
                    defaultData.schemas = val.schemas;
                    /* istanbul ignore else */
                    if (update) {
                        externalData.schemas = [...defaultData.schemas];
                    }
                }
                else if (update) {
                    externalData.schemas = [...defaultData.schemas, ...val.schemas];
                }
            }
        }
        // Exports
        const functions = {
            setDefault: setDefaultMetadata,
            set: setMetadata,
            reset: resetMetadata,
        };
        app.provide(METADATA, functions);
        app.provide('metadataData', externalData);
        app.config.globalProperties.$metadata = functions;
    },
};

export default index;
export { METADATA, script as Meta };
//# sourceMappingURL=v-meta.esm.js.map
